import { useMemo } from 'react'
import { isValidCEPSize } from 'src/components/checkout/steps/utils'
import { useAddressContext } from 'src/components/checkout/steps/shipping/context/address-context'
import { normalizePostalCode } from 'src/components/common/Header/PostalCode/utils/postalCode'
import { useCheckout } from 'src/sdk/checkout/useCheckout'
import type { ShippingCampaignEvaluation } from 'src/utils/shippingCampaign'
import { evaluateShippingCampaign } from 'src/utils/shippingCampaign'

type Params = {
  price?: number | null
  cashPrice?: number | null
  sellerName?: string | null
  categoryTree?: string[]
}

export function useProductCardShippingCampaign({
  price,
  cashPrice,
  sellerName,
  categoryTree,
}: Params): ShippingCampaignEvaluation {
  const { postalCode, address } = useAddressContext()
  const { orderForm } = useCheckout()

  const userState = useMemo(() => {
    if (!isValidCEPSize(postalCode)) {
      return undefined
    }

    if (address.state) {
      return address.state
    }

    const normalizedContextPostalCode = normalizePostalCode(postalCode)
    const normalizedOrderFormPostalCode = normalizePostalCode(
      orderForm.shippingData?.address?.postalCode
    )

    if (
      normalizedContextPostalCode !== normalizedOrderFormPostalCode ||
      !normalizedContextPostalCode
    ) {
      return undefined
    }

    return orderForm.shippingData?.address?.state ?? undefined
  }, [
    postalCode,
    address.state,
    orderForm.shippingData?.address?.postalCode,
    orderForm.shippingData?.address?.state,
  ])

  return useMemo(
    () =>
      evaluateShippingCampaign({
        price,
        cashPrice,
        sellerName,
        categoryTree,
        userState,
      }),
    [cashPrice, categoryTree, price, sellerName, userState]
  )
}
